import React, { useState, useEffect } from "react";

function MotoGame({ onFinish, disabled = false }) {
  const [selectedMotos, setSelectedMotos] = useState([]);
  const [revealedMotos, setRevealedMotos] = useState([]);
  const [intentosRestantes, setIntentosRestantes] = useState(3);
  const [premiosGanados, setPremiosGanados] = useState([]);
  const [juegoTerminado, setJuegoTerminado] = useState(false);
  const [yaJugo, setYaJugo] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [motosConPremio, setMotosConPremio] = useState([]);

  // Premios disponibles con sus probabilidades
  const premiosDisponibles = [
    { monto: "$5.000", probabilidad: 30 },
    { monto: "$10.000", probabilidad: 25 },
    { monto: "$15.000", probabilidad: 20 },
    { monto: "$20.000", probabilidad: 12 },
    { monto: "$30.000", probabilidad: 7 },
    { monto: "$35.000", probabilidad: 3 },
    { monto: "$40.000", probabilidad: 1.5 },
    { monto: "$45.000", probabilidad: 1 },
    { monto: "$50.000", probabilidad: 0.5 }
  ];

  const seleccionarPremioAleatorio = () => {
    const random = Math.random() * 100;
    let acumulado = 0;
    
    for (let premio of premiosDisponibles) {
      acumulado += premio.probabilidad;
      if (random <= acumulado) {
        return premio.monto;
      }
    }
    return premiosDisponibles[0].monto;
  };

  // Inicializar el juego
  useEffect(() => {
    if (!disabled && !yaJugo) {
      // Seleccionar 3 motos aleatorias que tendrán premios
      const posiciones = [];
      while (posiciones.length < 3) {
        const random = Math.floor(Math.random() * 15);
        if (!posiciones.includes(random)) {
          posiciones.push(random);
        }
      }
      
      // Asignar premios aleatorios basados en probabilidades a cada posición
      const motosConPremios = {};
      posiciones.forEach((pos) => {
        motosConPremios[pos] = seleccionarPremioAleatorio();
      });
      
      setMotosConPremio(motosConPremios);
      console.log("🏍️ Motos con premio:", motosConPremios);
    }
  }, [disabled, yaJugo]);

  const handleMotoClick = (motoIndex) => {
    if (isRevealing || juegoTerminado || yaJugo || disabled || selectedMotos.includes(motoIndex)) {
      return;
    }

    setIsRevealing(true);
    setSelectedMotos([...selectedMotos, motoIndex]);

    setTimeout(() => {
      setRevealedMotos([...revealedMotos, motoIndex]);
      
      // Verificar si ganó premio
      if (motosConPremio[motoIndex]) {
        const premio = motosConPremio[motoIndex];
        setPremiosGanados([...premiosGanados, premio]);
        setShowConfetti(true);
        
        setTimeout(() => setShowConfetti(false), 3000);
      }

      const nuevosIntentos = intentosRestantes - 1;
      setIntentosRestantes(nuevosIntentos);

      // Verificar si el juego terminó
      if (nuevosIntentos === 0) {
        finalizarJuego();
      }

      setIsRevealing(false);
    }, 1000);
  };

  const finalizarJuego = () => {
    setJuegoTerminado(true);
    setYaJugo(true);

    setTimeout(() => {
      let premioFinal;
      
      if (premiosGanados.length === 0) {
        // No ganó nada, recibe $5.000
        premioFinal = "$5.000";
      } else if (premiosGanados.length === 1) {
        premioFinal = premiosGanados[0];
      } else {
        // Ganó múltiples premios, sumar
        const total = premiosGanados.reduce((sum, premio) => {
          return sum + parseInt(premio.replace(/[$.]/g, ''));
        }, 0);
        premioFinal = `$${total.toLocaleString('es-CO')}`;
      }

      if (onFinish) {
        onFinish(premioFinal);
      }
    }, 2000);
  };

  const getTotalGanado = () => {
    if (premiosGanados.length === 0) return "$5.000";
    
    const total = premiosGanados.reduce((sum, premio) => {
      return sum + parseInt(premio.replace(/[$.]/g, ''));
    }, 0);
    
    return `$${total.toLocaleString('es-CO')}`;
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0a0a0a 0%, #1a0f00 25%, #2d1400 50%, #1a0f00 75%, #0a0a0a 100%)",
      position: "relative",
      overflow: "hidden",
      padding: "2rem 1rem"
    }}>
      {/* Efectos de fondo */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 106, 0, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 106, 0, 0.15) 0%, transparent 50%)
        `,
        pointerEvents: "none",
        animation: "pulse-bg 8s ease-in-out infinite"
      }} />

      {/* Nieve */}
      {[...Array(40)].map((_, i) => (
        <div
          key={i}
          style={{
            position: "fixed",
            top: "-10px",
            left: `${Math.random() * 100}%`,
            width: "8px",
            height: "8px",
            background: "white",
            borderRadius: "50%",
            opacity: Math.random() * 0.6 + 0.3,
            animation: `snowfall ${Math.random() * 3 + 5}s linear infinite`,
            animationDelay: `${Math.random() * 5}s`,
            pointerEvents: "none",
            zIndex: 1
          }}
        />
      ))}

      <style>{`
        @keyframes pulse-bg {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        
        @keyframes snowfall {
          to { transform: translateY(100vh); opacity: 0; }
        }
        
        @keyframes shake {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.05) rotate(-3deg); }
          75% { transform: scale(1.05) rotate(3deg); }
        }
        
        @keyframes motoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes glow {
          0%, 100% { 
            text-shadow: 0 0 20px rgba(255, 106, 0, 0.8),
                         0 0 40px rgba(255, 106, 0, 0.6);
          }
          50% { 
            text-shadow: 0 0 30px rgba(255, 106, 0, 1),
                         0 0 60px rgba(255, 106, 0, 0.8);
          }
        }
        
        @keyframes logoGlow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(255, 106, 0, 0.6)); }
          50% { filter: drop-shadow(0 0 35px rgba(255, 106, 0, 0.9)); }
        }
        
        @keyframes revealWin {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        
        @keyframes revealLose {
          0% { transform: scale(1); }
          50% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
      `}</style>

      <div style={{ position: "relative", zIndex: 2 }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ marginBottom: "1rem" }}>
            <img 
              src="https://invercycle.com/wp-content/uploads/2023/04/LOGOINVERCYCLE.png"
              alt="Invercycle Logo"
              style={{
                maxWidth: "250px",
                height: "auto",
                animation: "logoGlow 3s ease-in-out infinite"
              }}
            />
          </div>

          <h1 style={{
            fontSize: "clamp(2rem, 5vw, 4rem)",
            color: "#ff6a00",
            fontFamily: "Arial Black, sans-serif",
            textShadow: "0 0 20px rgba(255, 106, 0, 0.8), 3px 3px 8px rgba(0, 0, 0, 0.9)",
            marginBottom: "0.5rem",
            animation: "glow 3s ease-in-out infinite"
          }}>
            🏍️ ENCUENTRA LAS MOTOS PREMIADAS 🏍️
          </h1>

          <p style={{
            fontSize: "clamp(1rem, 3vw, 1.5rem)",
            color: "#ffde59",
            textShadow: "0 0 15px rgba(255, 222, 89, 0.8)",
            fontWeight: "700",
            marginBottom: "1rem"
          }}>
            3 de 15 motos tienen premios de $5.000 a $50.000
          </p>

          {/* Contador de intentos */}
          <div style={{
            display: "inline-block",
            background: "rgba(0, 0, 0, 0.8)",
            border: "3px solid #ff6a00",
            borderRadius: "15px",
            padding: "1rem 2rem",
            marginBottom: "1rem"
          }}>
            <div style={{
              fontSize: "1.5rem",
              color: "#ff9d00",
              fontWeight: "bold",
              textShadow: "0 0 10px rgba(255, 157, 0, 0.8)"
            }}>
              Intentos restantes: <span style={{ fontSize: "2rem", color: "#ff6a00" }}>{intentosRestantes}</span>
            </div>
          </div>

          {premiosGanados.length > 0 && (
            <div style={{
              display: "inline-block",
              background: "rgba(255, 106, 0, 0.2)",
              border: "3px solid #ff6a00",
              borderRadius: "15px",
              padding: "1rem 2rem",
              marginLeft: "1rem",
              animation: "bounce 2s ease-in-out infinite"
            }}>
              <div style={{
                fontSize: "1.2rem",
                color: "#ffde59",
                fontWeight: "bold"
              }}>
                💰 Premios ganados: {premiosGanados.join(", ")}
              </div>
            </div>
          )}
        </div>

        {!juegoTerminado && (
          <p style={{
            textAlign: "center",
            fontSize: "1.2rem",
            color: "#ff9d00",
            marginBottom: "2rem",
            textShadow: "0 0 10px rgba(255, 157, 0, 0.8)",
            fontWeight: "600"
          }}>
            👆 Escoge 3 motos y descubre si ganaste
          </p>
        )}

        {/* Grid de Motos */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "1.5rem",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "2rem"
        }}>
          {[...Array(15)].map((_, index) => {
            const isSelected = selectedMotos.includes(index);
            const isRevealed = revealedMotos.includes(index);
            const tienePremio = motosConPremio[index];
            const mostrarPremio = isRevealed && tienePremio;
            const mostrarVacio = isRevealed && !tienePremio;

            return (
              <div
                key={index}
                onClick={() => handleMotoClick(index)}
                style={{
                  cursor: (isRevealing || juegoTerminado || yaJugo || disabled || isSelected) ? "not-allowed" : "pointer",
                  transition: "all 0.4s ease",
                  animation: isSelected && !isRevealed ? "shake 0.5s ease-in-out" : 
                             !isSelected && !juegoTerminado ? "motoPulse 2s ease-in-out infinite" : "none",
                  animationDelay: `${index * 0.05}s`,
                  opacity: juegoTerminado && !isSelected ? 0.4 : 1,
                  pointerEvents: (isRevealing || juegoTerminado || yaJugo || disabled || isSelected) ? "none" : "auto",
                  transform: isSelected ? "scale(1.05)" : "scale(1)"
                }}
                onMouseEnter={(e) => {
                  if (!isRevealing && !juegoTerminado && !yaJugo && !disabled && !isSelected) {
                    e.currentTarget.style.transform = "scale(1.1) translateY(-8px)";
                    e.currentTarget.style.filter = "drop-shadow(0 10px 25px rgba(255, 106, 0, 0.6))";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isRevealing && !juegoTerminado && !yaJugo && !disabled && !isSelected) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.filter = "none";
                  }
                }}
              >
                <div style={{
                  background: mostrarPremio ? "linear-gradient(135deg, #ffde59, #ff6a00)" :
                              mostrarVacio ? "linear-gradient(135deg, #3a3a3a, #1a1a1a)" :
                              "linear-gradient(135deg, #1a1a1a, #0a0a0a)",
                  borderRadius: "20px",
                  padding: "1.5rem",
                  height: "180px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `3px solid ${mostrarPremio ? "#ff6a00" : "rgba(255, 106, 0, 0.3)"}`,
                  boxShadow: mostrarPremio ? "0 0 30px rgba(255, 106, 0, 0.8)" :
                             "0 10px 25px rgba(0, 0, 0, 0.5)",
                  position: "relative",
                  overflow: "hidden",
                  animation: mostrarPremio ? "revealWin 0.6s ease-out" :
                             mostrarVacio ? "revealLose 0.6s ease-out" : "none"
                }}>
                  {/* Número de moto */}
                  <div style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    background: "rgba(255, 106, 0, 0.3)",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    color: "#ff9d00"
                  }}>
                    {index + 1}
                  </div>

                  {!isRevealed && (
                    <>
                      <div style={{ fontSize: "4rem", marginBottom: "0.5rem" }}>🏍️</div>
                      <div style={{
                        fontSize: "0.9rem",
                        color: "#ff9d00",
                        fontWeight: "bold",
                        textAlign: "center",
                        textShadow: "0 0 10px rgba(255, 157, 0, 0.8)"
                      }}>
                        MOTO #{index + 1}
                      </div>
                    </>
                  )}

                  {mostrarPremio && (
                    <>
                      <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🎉</div>
                      <div style={{
                        fontSize: "1.8rem",
                        color: "#1a1a1a",
                        fontWeight: "bold",
                        textShadow: "0 0 10px rgba(255, 222, 89, 0.8)"
                      }}>
                        {tienePremio}
                      </div>
                      <div style={{
                        fontSize: "0.9rem",
                        color: "#1a1a1a",
                        fontWeight: "bold",
                        marginTop: "0.3rem"
                      }}>
                        ¡GANASTE!
                      </div>
                    </>
                  )}

                  {mostrarVacio && (
                    <>
                      <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>❌</div>
                      <div style={{
                        fontSize: "1.2rem",
                        color: "#666",
                        fontWeight: "bold"
                      }}>
                        SIN PREMIO
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Confetti */}
        {showConfetti && [...Array(50)].map((_, i) => (
          <div
            key={i}
            style={{
              position: "fixed",
              top: "20%",
              left: "50%",
              width: "10px",
              height: "10px",
              background: ["#ff6a00", "#ff9d00", "#ffde59", "#1a1a1a"][i % 4],
              animation: "confetti 3s ease-out forwards",
              animationDelay: `${i * 0.02}s`,
              zIndex: 100,
              transform: `translate(${Math.random() * 400 - 200}px, ${Math.random() * 100}px)`,
              borderRadius: i % 2 === 0 ? "50%" : "0"
            }}
          />
        ))}

        {/* Modal Final */}
        {juegoTerminado && (
          <div style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(0, 0, 0, 0.97)",
            backdropFilter: "blur(15px)",
            border: "5px solid #ff6a00",
            borderRadius: "30px",
            padding: "3rem 2.5rem",
            maxWidth: "600px",
            width: "92%",
            boxShadow: "0 0 60px rgba(255, 106, 0, 0.9)",
            zIndex: 1000,
            animation: "fadeInScale 0.5s ease-out",
            textAlign: "center"
          }}>
            <div style={{
              fontSize: "clamp(2rem, 5vw, 3rem)",
              color: "#ffde59",
              fontWeight: "bold",
              marginBottom: "1.5rem",
              textShadow: "0 0 25px rgba(255, 222, 89, 0.9)",
              animation: "bounce 1s ease-in-out infinite"
            }}>
              🎉 ¡FELICITACIONES! 🎉
            </div>

            <div style={{
              fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
              color: "#ff6a00",
              fontWeight: "bold",
              textShadow: "0 0 30px rgba(255, 106, 0, 1)"
            }}>
              {getTotalGanado()}
            </div>

            {premiosGanados.includes("$50.000") && (
              <div style={{
                marginTop: "1.5rem",
                fontSize: "2.5rem",
                animation: "bounce 1s ease-in-out infinite",
                color: "#ffde59"
              }}>
                🎊 ¡PREMIO MAYOR! 🎊
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MotoGame;